import { useEffect, useState } from 'react'

import SearchIcon from '@heroicons/react/solid/esm/SearchIcon.js'
import { useMatch } from 'react-router'

import { SearchEntity, SearchEntityType } from '@tribeplatform/gql-client/types'
import { useRouter } from '@tribeplatform/react-sdk'
import { useSearch } from '@tribeplatform/react-sdk/hooks'
import { getSearchKey } from '@tribeplatform/react-sdk/utils/keys'
import { Autocomplete } from '@tribeplatform/react-ui-kit/Autocomplete'
import { SpinnerIcon } from '@tribeplatform/react-ui-kit/icons'
import { Input } from '@tribeplatform/react-ui-kit/Input'

import { useDebounce } from '../common/hooks/useDebounce.js'
import { useFirstRenderContext } from '../common/ssr/FirstRenderProvider.js'
import { T } from '../i18n/components/T.js'
import { useI18n } from '../i18n/providers/I18nProvider.js'
import { cleanSearchQuery } from '../utils/search.js'
import { SearchAutocompleteItems } from './SearchAutocompleteItems.js'
import { SearchRowPage } from './SearchRow.js'
import { getSearchItemLink, getSearchPageLink } from './utils.js'

const PAGE_RESULT = 'search-page-shortcut'

interface SearchBarProps {
  autoFocus?: boolean
  query?: string
  type?: string
  entityType?: string
  placeholder?: string
}

export const SearchBar = ({
  autoFocus = false,
  query: inputQuery = '',
  type = null,
  entityType = null,
  placeholder: maybePlaceholder,
}: SearchBarProps) => {
  const { $t } = useI18n()
  const [dirtyQuery, setDirtyQuery] = useState('')
  const query = cleanSearchQuery(dirtyQuery)

  const { push, replace } = useRouter()
  const debouncedQuery = useDebounce(query, 300)
  const match = useMatch('/search')
  const enabled = match === null

  useEffect(() => {
    setDirtyQuery(inputQuery || '')
  }, [inputQuery, type])

  const { data, isInitialLoading, isFetching } = useSearch({
    variables: {
      input: {
        query: debouncedQuery
          ? debouncedQuery + (entityType ? ` +for:${entityType}` : '')
          : '',
        includeExternal: true,
      },
    },
    fields: 'all',
    useQueryOptions: {
      queryKey: getSearchKey({
        variables: {
          input: { query: debouncedQuery },
        },
      }),
      keepPreviousData: true,
      enabled,
    },
  })

  const onSelect = (value: SearchEntity) => {
    if (value && value.entityType !== SearchEntityType.External) {
      if ((value as any) === PAGE_RESULT) {
        push(getSearchPageLink({ search: dirtyQuery, type }))
      } else {
        push(getSearchItemLink(value))
      }
    }
  }

  const onEnter = ({ highlightedIndex, inputValue }) => {
    if (inputValue && highlightedIndex < 1) {
      push(getSearchPageLink({ search: dirtyQuery, type }))
    }
  }

  const placeholder =
    maybePlaceholder ??
    $t({
      id: 'Generics.SearchDotDotDot',
      defaultMessage: 'Search...',
    })

  const { isFirstRender } = useFirstRenderContext()
  if (isFirstRender) {
    /**
     * @tag Vite migration
     * Autocomplete produces hydration error because of prop `id` did not match.
     * As a workaround, Autocomplete is initialized client-side only
     */
    return (
      <div className="flex items-center flex-1">
        <Input
          value={dirtyQuery}
          onChange={e => setDirtyQuery(e.target.value)}
          leadingIcon={<SearchIcon />}
          placeholder={placeholder}
        />
      </div>
    )
  }

  return (
    <div className="flex items-center flex-1">
      <Autocomplete
        id="searchBar"
        itemToString={value => (value as SearchEntity)?.title || ''}
        value={dirtyQuery}
        onChange={onSelect}
        options={[
          PAGE_RESULT,
          ...(data?.hits.flatMap(category => category.hits) || []),
        ]}
        onInputChange={query => {
          setDirtyQuery(query)
          if (!enabled) {
            replace(getSearchPageLink({ search: query, type }))
          }
        }}
        className="w-full relative"
        loading={isInitialLoading}
        defaultHighlightedIndex={enabled ? 0 : undefined}
        clearInputOnSelect={enabled}
        clearInputOnBlur={enabled}
      >
        <Autocomplete.Input
          autoFocus={autoFocus}
          leadingIcon={
            enabled && isFetching ? (
              <SpinnerIcon className="animate-spin" />
            ) : (
              <SearchIcon />
            )
          }
          placeholder={placeholder}
          onEnter={onEnter}
          showClearButton={false}
          inputMode="search"
        />
        {enabled && (
          <Autocomplete.Items className="h-full overflow-y-auto isolate">
            <Autocomplete.Item index={0} value="search-page-shortcut-value">
              <SearchRowPage
                search={dirtyQuery}
                link={getSearchPageLink({ search: dirtyQuery, type })}
                spacing="sm"
              />
            </Autocomplete.Item>
            <SearchAutocompleteItems data={data} />
            {isInitialLoading && (
              <Autocomplete.ItemsEmpty>
                <T
                  id="Generics.SearchingDotDotDto"
                  defaultMessage="Searching..."
                />
              </Autocomplete.ItemsEmpty>
            )}
          </Autocomplete.Items>
        )}
      </Autocomplete>
    </div>
  )
}
